import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Home } from "lucide-react";

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      <Helmet>
        <title>Terms of Service | Its-Sold.com</title>
        <meta name="description" content="Terms of Service for Its-Sold.com - DFW's free FSBO listing platform." />
        <link rel="canonical" href="https://its-sold.com/terms" />
      </Helmet>
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-5xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Playfair Display' }}>
          Terms of Service
        </h1>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Free Listing Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>Duration:</strong> Free listings remain active for 90 days from creation date. After 90 days, listings automatically expire unless renewed or upgraded.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>Renewal:</strong> Free listings may be renewed for $29/month after initial 90-day period.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Upgrades:</strong> Featured ($49/month) and Premium ($99/month) plans remain active as long as subscription is maintained.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. agent Disclosure & Anti-Harassment</h2>
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 mb-4">
              <h3 className="text-xl font-bold text-orange-900 mb-3">🏛️ MANDATORY DISCLOSURE</h3>
              <p className="text-orange-800 mb-3">
                <strong>All Texas Real Estate Licensees MUST:</strong>
              </p>
              <ul className="space-y-2 text-orange-800">
                <li>• Disclose agent status during signup</li>
                <li>• Provide valid Texas Real Estate Commission (TREC) license number</li>
                <li>• Identify themselves as agents in all communications with sellers</li>
                <li>• Respect seller preference to sell FSBO</li>
              </ul>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>Prohibited Conduct:</strong> agents may NOT contact FSBO sellers for solicitation purposes. 
              Genuine buyer inquiries are permitted but seller has right to verify and report misrepresentation.
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>Seller Protection:</strong> Every inquiry includes a "Report Harassment" button. Reports are filed with 
              the agent's license number on record and may result in:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mb-3">
              <li>Account warning (2 reports)</li>
              <li>Account suspension (3 reports)</li>
              <li>Referral to TREC for investigation</li>
              <li>Potential license sanctions</li>
            </ul>
            
            <p className="text-gray-700 leading-relaxed">
              <strong>False Reporting:</strong> Sellers who file false harassment claims may have their accounts suspended.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Anti-Scraping & Data Protection</h2>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-4">
              <h3 className="text-xl font-bold text-red-900 mb-3">⚠️ PROHIBITED ACTIVITIES</h3>
              <ul className="space-y-2 text-red-800">
                <li>• <strong>Data Scraping:</strong> Automated extraction of listing data, images, or contact information</li>
                <li>• <strong>Content Harvesting:</strong> Bulk downloading or copying of property listings</li>
                <li>• <strong>API Abuse:</strong> Excessive requests or attempts to circumvent rate limits</li>
                <li>• <strong>Competitor Surveillance:</strong> Systematic monitoring for competitive intelligence</li>
                <li>• <strong>Contact Info Harvesting:</strong> Collection of seller contact details for unauthorized marketing</li>
              </ul>
            </div>
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>Enforcement:</strong> Violations will result in immediate account termination and may result in legal action including claims for damages under the Computer Fraud and Abuse Act (CFAA) and state law.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Detection:</strong> We employ advanced fingerprinting, rate limiting, and behavioral analysis to detect scraping activity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Listing Content Requirements</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              All listings must contain accurate, truthful information. Sellers are responsible for:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Accurate property descriptions and pricing</li>
              <li>Truthful disclosure of property conditions</li>
              <li>Ownership rights to all photos/videos uploaded</li>
              <li>Compliance with Fair Housing laws</li>
              <li>Timely updates if property status changes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Privacy & Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>Authenticated Users:</strong> Full contact information is displayed to authenticated users only.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Non-Authenticated Visitors:</strong> Contact details are partially obscured to prevent spam and unauthorized collection.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Refund Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              All sales are final. Featured and Premium listing upgrades are non-refundable once payment has been processed.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>No Refunds:</strong> Payment for services and listing upgrades are final and non-refundable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              All content on Its-Sold.com, including logos, design, and AI-generated content, is protected by copyright and trademark laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Sellers retain ownership of uploaded photos/videos but grant Its-Sold.com a license to display them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              Its-Sold.com is a platform connecting buyers and sellers. We are not real estate agents or brokers. All transactions are between buyers and sellers directly. We provide tools and exposure but make no guarantees about sale outcomes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Texas-Specific Provisions</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Sellers must comply with Texas Property Code disclosure requirements. Its-Sold.com provides templates but sellers are responsible for legal compliance.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Survey Requirements:</strong> Texas law may require property surveys. Sellers should indicate survey availability in listings.
            </p>
          </section>

          <section className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-xl font-bold text-blue-900 mb-3">📧 Contact & Reporting</h3>
            <p className="text-blue-800 mb-2">
              <strong>Report Violations:</strong> abuse@its-sold.com
            </p>
            <p className="text-blue-800 mb-2">
              <strong>Legal Inquiries:</strong> legal@its-sold.com
            </p>
            <p className="text-blue-800">
              <strong>Support:</strong> support@its-sold.com
            </p>
          </section>

          <div className="pt-8 border-t border-gray-200 text-center text-gray-600">
            <p>Last Updated: December 2, 2025</p>
            <p className="mt-2">By using Its-Sold.com, you agree to these Terms of Service</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
